<template>
  <main class="wallet-user-guide-reward-page">
    <AppNavBar
      :title="t('wallet.userGuideRewardRulesTitle')"
      background-color="#ffffff"
    />

    <section class="reward-guide-content">
      <section class="reward-guide-notice">
        <h2>{{ t("wallet.userGuideRewardNoticeTitle") }}</h2>
        <p v-for="(notice, index) in notices" :key="index">
          <span class="reward-guide-dot"></span>
          <span>
            <template
              v-for="(segment, segmentIndex) in highlightText(notice)"
              :key="`${index}-${segmentIndex}`"
            >
              <span v-if="segment.highlight" class="reward-guide-highlight">
                {{ segment.text }}
              </span>
              <span v-else-if="segment.bold" class="reward-guide-bold">
                {{ segment.text }}
              </span>
              <template v-else>{{ segment.text }}</template>
            </template>
          </span>
        </p>
      </section>

      <article class="reward-guide-item">
        <h2 class="reward-guide-question">
          <span class="reward-guide-question__icon" aria-hidden="true">
            <app-image :src="questionIcon" alt="" />
          </span>
          <span>{{ t("wallet.userGuideRewardQuestion1") }}</span>
        </h2>
        <p class="reward-guide-answer">
          {{ t("wallet.userGuideRewardAnswer1") }}
        </p>
      </article>

      <article class="reward-guide-item">
        <h2 class="reward-guide-question">
          <span class="reward-guide-question__icon" aria-hidden="true">
            <app-image :src="questionIcon" alt="" />
          </span>
          <span>{{ t("wallet.userGuideRewardQuestion2") }}</span>
        </h2>
        <p class="reward-guide-answer">
          {{ t("wallet.userGuideRewardAnswer2") }}
        </p>
      </article>

      <article class="reward-guide-item reward-guide-item--consumption">
        <h2 class="reward-guide-question">
          <span class="reward-guide-question__icon" aria-hidden="true">
            <app-image :src="questionIcon" alt="" />
          </span>
          <span>{{ t("wallet.userGuideRewardQuestion3") }}</span>
        </h2>
        <div class="reward-guide-answer reward-guide-answer--sections">
          <p v-for="(section, index) in consumptionRules" :key="index">
            <strong>{{ section.label }}</strong>
            <span> —— {{ section.text }}</span>
          </p>
        </div>
      </article>

      <article class="reward-guide-item">
        <h2 class="reward-guide-question">
          <span class="reward-guide-question__icon" aria-hidden="true">
            <app-image :src="questionIcon" alt="" />
          </span>
          <span>{{ t("wallet.userGuideRewardQuestion4") }}</span>
        </h2>
        <p class="reward-guide-answer">
          {{ t("wallet.userGuideRewardAnswer4") }}
        </p>
      </article>

      <article class="reward-guide-item reward-guide-item--levels">
        <h2 class="reward-guide-question">
          <span class="reward-guide-question__icon" aria-hidden="true">
            <app-image :src="questionIcon" alt="" />
          </span>
          <span>{{ t("wallet.userGuideRewardQuestion5") }}</span>
        </h2>
        <div class="reward-guide-answer reward-guide-levels">
          <p class="label">{{ t("wallet.userGuideRewardAnswer5") }}</p>
          <div class="reward-guide-table-wrapper">
            <table class="reward-guide-table">
              <thead>
                <tr>
                  <th>{{ t("wallet.userGuideRewardTableLevel") }}</th>
                  <th>{{ t("wallet.userGuideRewardTableCondition") }}</th>
                  <th>{{ t("wallet.userGuideRewardTableCard") }}</th>
                  <th>{{ t("wallet.userGuideRewardTableSpending") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(level, index) in levels" :key="index">
                  <td>{{ level.name }}</td>
                  <td>{{ level.condition }}</td>
                  <td>{{ level.card }}</td>
                  <td>{{ level.spending }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="reward-guide-contact">
            <p class="label">
            {{ t("wallet.userGuideRewardContactPrefix") }}
            <span>{{ t("wallet.userGuideRewardContactEmail") }}</span>
            </p>
          </p>
        </div>
      </article>

      <p class="reward-guide-disclaimer">
        {{ t("wallet.userGuideRewardDisclaimer") }}
      </p>
    </section>
  </main>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import AppNavBar from "@/components/AppNavBar.vue";
import questionIcon from "@/assets/images/SVG/icon/question.svg";

const { t } = useI18n();

const notices = computed(() => [
  t("wallet.userGuideRewardNotice1"),
  t("wallet.userGuideRewardNotice2"),
  t("wallet.userGuideRewardNotice3"),
]);

const consumptionRules = computed(() => [
  {
    label: t("wallet.userGuideRewardConsumption1Label"),
    text: t("wallet.userGuideRewardConsumption1Text"),
  },
  {
    label: t("wallet.userGuideRewardConsumption2Label"),
    text: t("wallet.userGuideRewardConsumption2Text"),
  },
  {
    label: t("wallet.userGuideRewardConsumption3Label"),
    text: t("wallet.userGuideRewardConsumption3Text"),
  },
  {
    label: t("wallet.userGuideRewardConsumption4Label"),
    text: t("wallet.userGuideRewardConsumption4Text"),
  },
]);

const levels = computed(() => [
  {
    name: t("wallet.userGuideRewardLevel1"),
    condition: t("wallet.userGuideRewardLevel1Condition"),
    card: "25%",
    spending: "0.03%",
  },
  {
    name: t("wallet.userGuideRewardLevel2"),
    condition: t("wallet.userGuideRewardLevel2Condition"),
    card: "30%",
    spending: "0.05%",
  },
  {
    name: t("wallet.userGuideRewardLevel3"),
    condition: t("wallet.userGuideRewardLevel3Condition"),
    card: "35%",
    spending: "0.08%",
  },
  {
    name: t("wallet.userGuideRewardPartnerLevel"),
    condition: t("wallet.userGuideRewardPartnerCondition"),
    card: "60%",
    spending: "0.1%",
  },
]);

function highlightText(text) {
  const segments = [];
  const pattern = /\[\[([\s\S]*?)\]\]|##([\s\S]*?)##/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text))) {
    if (match.index > lastIndex) {
      segments.push({
        text: text.slice(lastIndex, match.index),
        highlight: false,
      });
    }
    segments.push({
      text: match[1] ?? match[2],
      highlight: Boolean(match[1]),
      bold: Boolean(match[2]),
    });
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), highlight: false });
  }

  return segments;
}
</script>

<style scoped lang="scss">
.wallet-user-guide-reward-page {
  min-height: var(--app-viewport-height);
  background: #ffffff;
}

.reward-guide-content {
  padding: 8px 14px calc(24px + env(safe-area-inset-bottom));
}

.reward-guide-notice {
  margin-bottom: 17px;
  padding: 20px 16px;
  border-radius: 16px;
  background: #eaf2ff;
  color: #333333;
  font-size: 14px;
}

.reward-guide-notice h2 {
  margin: 0 0 4px;
  color: #14234b;
  font-size: 16px;
  font-weight: 700;
}

.reward-guide-notice p {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  font-weight: 500;
}

.reward-guide-dot {
  display: block;
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin-right: 8px;
  border-radius: 50%;
  background: #2667ff;
  margin-top: 3px;
}

.reward-guide-highlight {
  color: #2667ff;
  font-weight: 700;
}

.reward-guide-bold {
  font-weight: 700;
}

.reward-guide-item {
  margin-bottom: 20px;
}

.reward-guide-question {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 0 0 11px;
  color: #14234b;
  font-size: 16px;
  font-weight: 700;
}

.reward-guide-question__icon {
  display: inline-flex;
  flex: 0 0 18px;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 1px;

  .app-image {
    width: 18px;
    height: 18px;
  }
}

.reward-guide-answer {
  margin: 0 0 0 8px;
  padding: 0 0 0 11px;
  border-left: 1px solid #e5e5e5;
  color: #333333;
  font-size: 14px;
  font-weight: 500;
}

.reward-guide-answer--sections p {
  margin: 0 0 9px;
}

.reward-guide-answer--sections p:last-child {
  margin-bottom: 0;
}

.reward-guide-answer strong {
  color: #333333;
  font-weight: 500;
}

.reward-guide-levels p {
  margin: 0 0 7px;
}

.reward-guide-table-wrapper {
  overflow: hidden;
  border-radius: 12px;
}

.reward-guide-table {
  width: calc(100% + 11px);
  border-collapse: collapse;
  table-layout: fixed;
  color: #333333;
  font-size: 11px;
  line-height: 16px;
  text-align: center;
}

.reward-guide-table th,
.reward-guide-table td {
  height: 38px;
  padding: 4px 2px;
  border-right: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  font-weight: 400;
  word-break: break-word;
}

.reward-guide-table th {
  height: 36px;
  background: #f4f5f6;
  color: #222222;
}

.reward-guide-table tr:last-child td {
  border-bottom: 0;
}

.reward-guide-table th:last-child,
.reward-guide-table td:last-child {
  border-right: 0;
}

.reward-guide-contact {
  margin: 7px 0 0 !important;
  font-size: 11px;
  white-space: nowrap;
  padding-bottom: 12px;
  border-bottom: 1px dashed #eee;
}

.reward-guide-contact span {
  color: #2667ff;
}

.reward-guide-disclaimer {
  margin: 12px 0 0 8px;
  padding-left: 11px;
  border-left: 1px solid #e5e5e5;
  color: #666;
  font-size: 12px;
  line-height: 18px;
}

.reward-guide-levels {
  border-left: 0;
  padding: 0;


  .label  {
    padding-left: 11px;
    border-left: 1px solid #e5e5e5;
    white-space: pre-wrap;
  }

  .reward-guide-table-wrapper {
    border: 1px solid #f4f5f6;
    margin-bottom: 14px;
  }
}
</style>
